import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import {
  CheckCircle2,
  Loader2,
  AlertTriangle,
  ShoppingBag,
  Users,
  LayoutDashboard,
  Sparkles,
  Home,
} from 'lucide-react';

/**
 * Landing page dopo la conferma email inviata via Supabase.
 * L'URL contiene i token nell'hash (#access_token=...&refresh_token=...&type=signup).
 * La pagina:
 *   1) legge l'hash
 *   2) chiama supabase.auth.setSession per attivare la sessione
 *   3) rileva il ruolo (client/crafter) dal profilo
 *   4) mostra il messaggio di benvenuto + CTA verso destinazioni utili
 */
export default function EmailConfirmedPage() {
  const navigate = useNavigate();
  const { refreshUser, profile } = useAuth();
  const [status, setStatus] = useState('processing'); // processing | success | error | expired
  const [errorMsg, setErrorMsg] = useState(null);

  useEffect(() => {
    const run = async () => {
      try {
        // Supabase può restituire i token in 3 modi diversi a seconda del flow:
        //  1) PKCE (default nuovo): ?code=xxx nel query string → exchangeCodeForSession
        //  2) Legacy implicit: #access_token=...&refresh_token=... nell'hash → setSession
        //  3) SDK ha già rilevato la sessione automaticamente (detectSessionInUrl=true)
        //
        // Ordine: prima controlliamo se la sessione è già attiva (SDK l'ha gestita),
        // poi proviamo il code exchange, infine il fallback hash tokens.

        // Piccola attesa per lasciare al SDK il tempo di processare l'URL
        await new Promise((r) => setTimeout(r, 100));

        // 0) Controlla se il SDK ha già impostato una sessione via detectSessionInUrl
        const { data: existing } = await supabase.auth.getSession();
        if (existing?.session) {
          window.history.replaceState({}, document.title, window.location.pathname);
          try { await refreshUser?.(); } catch (_) { /* non-blocking */ }
          setStatus('success');
          return;
        }

        // Parametri da query string (PKCE) e hash (implicit)
        const urlParams = new URLSearchParams(window.location.search);
        const code = urlParams.get('code');
        const queryError = urlParams.get('error') || urlParams.get('error_code');
        const queryErrorDesc = urlParams.get('error_description');

        const hash = window.location.hash?.replace(/^#/, '') || '';
        const hashParams = new URLSearchParams(hash);
        const accessToken = hashParams.get('access_token');
        const refreshToken = hashParams.get('refresh_token');
        const hashError = hashParams.get('error') || hashParams.get('error_code');
        const hashErrorDesc = hashParams.get('error_description');

        const errorParam = queryError || hashError;
        const errorDescription = queryErrorDesc || hashErrorDesc;

        if (errorParam || errorDescription) {
          const isExpired = /expired|invalid/i.test(`${errorParam} ${errorDescription}`);
          setStatus(isExpired ? 'expired' : 'error');
          setErrorMsg(errorDescription || errorParam);
          return;
        }

        // 1) PKCE flow: ?code=xxx
        if (code) {
          const { error: exchErr } = await supabase.auth.exchangeCodeForSession(code);
          if (exchErr) {
            const msg = exchErr.message || 'Impossibile confermare il codice.';
            const isExpired = /expired|invalid|used/i.test(msg);
            setStatus(isExpired ? 'expired' : 'error');
            setErrorMsg(msg);
            return;
          }
          window.history.replaceState({}, document.title, window.location.pathname);
          try { await refreshUser?.(); } catch (_) { /* non-blocking */ }
          setStatus('success');
          return;
        }

        // 2) Legacy implicit flow: hash tokens
        if (accessToken && refreshToken) {
          const { error } = await supabase.auth.setSession({
            access_token: accessToken,
            refresh_token: refreshToken,
          });
          if (error) {
            setStatus('error');
            setErrorMsg(error.message || 'Impossibile attivare la sessione.');
            return;
          }
          window.history.replaceState({}, document.title, window.location.pathname);
          try { await refreshUser?.(); } catch (_) { /* non-blocking */ }
          setStatus('success');
          return;
        }

        // 3) Nessun token/codice trovato
        setStatus('error');
        setErrorMsg('Link non valido o già utilizzato.');
      } catch (e) {
        setStatus('error');
        setErrorMsg(e?.message || 'Errore inatteso.');
      }
    };
    run();
  }, []);

  // --- Rendering per stato ---
  if (status === 'processing') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 px-4">
        <div className="text-center" data-testid="email-confirm-loading">
          <Loader2 className="w-10 h-10 mx-auto text-shop-primary animate-spin mb-4" />
          <p className="text-slate-600">Sto confermando la tua registrazione...</p>
        </div>
      </div>
    );
  }

  if (status === 'error' || status === 'expired') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 px-4">
        <div
          className="max-w-md w-full bg-white rounded-2xl border border-slate-200 p-8 text-center shadow-sm"
          data-testid="email-confirm-error"
        >
          <AlertTriangle className="w-12 h-12 mx-auto text-amber-500 mb-4" />
          <h1 className="text-2xl font-semibold text-slate-800 mb-2">
            {status === 'expired'
              ? 'Link di conferma scaduto'
              : 'Non abbiamo potuto confermare la registrazione'}
          </h1>
          <p className="text-slate-600 mb-6">
            {status === 'expired'
              ? 'Il link è scaduto o è già stato usato. Torna alla pagina di login e clicca "Rispedisci email di conferma".'
              : errorMsg || 'Prova a ripetere l\'operazione dal tuo account.'}
          </p>
          <Button asChild className="bg-shop-primary hover:bg-shop-primary/90 text-white">
            <Link to="/login">Vai al login</Link>
          </Button>
        </div>
      </div>
    );
  }

  // status === 'success'
  const isCrafter = profile?.role === 'crafter' || profile?.is_crafter_enabled === true;
  const firstName = (profile?.full_name || '').split(' ')[0] || 'benvenuto';

  return (
    <div className="min-h-screen bg-gradient-to-b from-teal-50/50 to-white py-12 px-4">
      <div className="max-w-3xl mx-auto" data-testid="email-confirm-success">
        {/* Hero */}
        <div className="text-center mb-10">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-emerald-100 text-emerald-600 mb-4">
            <CheckCircle2 className="w-9 h-9" />
          </div>
          <h1 className="text-3xl sm:text-4xl font-bold text-slate-800 mb-3">
            Registrazione confermata!
          </h1>
          <p className="text-base sm:text-lg text-slate-600 max-w-xl mx-auto leading-relaxed">
            Ciao <span className="font-semibold">{firstName}</span>, benvenuto su Rarifex 🎉
            {isCrafter
              ? ' La tua vetrina è pronta: carica le tue creazioni e inizia a raccontarti.'
              : ' Ora fai parte della community che sostiene l\'artigianato Made in Italy.'}
          </p>
        </div>

        {/* Suggerimenti dove andare */}
        <div className="mb-6 text-center">
          <p className="text-xs uppercase tracking-widest text-slate-400 font-semibold">
            Da dove vuoi cominciare?
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {isCrafter && (
            <Link
              to="/dashboard?tab=artigiano&section=home"
              className="group bg-white rounded-xl border-2 border-dashed border-teal-200 hover:border-teal-400 hover:bg-teal-50/30 transition-all p-5 flex items-start gap-3"
              data-testid="cta-crafter-dashboard"
            >
              <div className="w-11 h-11 rounded-lg bg-teal-100 text-teal-700 flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform">
                <LayoutDashboard className="w-5 h-5" />
              </div>
              <div className="text-left">
                <p className="font-semibold text-slate-800">La mia Dashboard</p>
                <p className="text-sm text-slate-500 leading-relaxed">
                  Gestisci vetrina, prodotti, ordini e regole di spedizione.
                </p>
              </div>
            </Link>
          )}

          <Link
            to="/products"
            className="group bg-white rounded-xl border-2 border-dashed border-teal-200 hover:border-teal-400 hover:bg-teal-50/30 transition-all p-5 flex items-start gap-3"
            data-testid="cta-catalog-products"
          >
            <div className="w-11 h-11 rounded-lg bg-amber-100 text-amber-700 flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform">
              <ShoppingBag className="w-5 h-5" />
            </div>
            <div className="text-left">
              <p className="font-semibold text-slate-800">Catalogo prodotti</p>
              <p className="text-sm text-slate-500 leading-relaxed">
                Scopri le creazioni degli artigiani italiani, filtrate per categoria.
              </p>
            </div>
          </Link>

          <Link
            to="/crafters"
            className="group bg-white rounded-xl border-2 border-dashed border-teal-200 hover:border-teal-400 hover:bg-teal-50/30 transition-all p-5 flex items-start gap-3"
            data-testid="cta-catalog-crafters"
          >
            <div className="w-11 h-11 rounded-lg bg-rose-100 text-rose-700 flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform">
              <Users className="w-5 h-5" />
            </div>
            <div className="text-left">
              <p className="font-semibold text-slate-800">Artigiani italiani</p>
              <p className="text-sm text-slate-500 leading-relaxed">
                Sfoglia le vetrine di chi crea con le mani, in tutta Italia.
              </p>
            </div>
          </Link>

          <Link
            to="/requests"
            className="group bg-white rounded-xl border-2 border-dashed border-teal-200 hover:border-teal-400 hover:bg-teal-50/30 transition-all p-5 flex items-start gap-3"
            data-testid="cta-custom-requests"
          >
            <div className="w-11 h-11 rounded-lg bg-violet-100 text-violet-700 flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform">
              <Sparkles className="w-5 h-5" />
            </div>
            <div className="text-left">
              <p className="font-semibold text-slate-800">Richieste su misura</p>
              <p className="text-sm text-slate-500 leading-relaxed">
                Cerchi qualcosa di unico? Chiedilo agli artigiani della piattaforma.
              </p>
            </div>
          </Link>
        </div>

        {/* Ritorno home */}
        <div className="text-center mt-8">
          <button
            type="button"
            onClick={() => navigate('/')}
            className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-slate-700 transition-colors"
            data-testid="cta-back-home"
          >
            <Home className="w-4 h-4" />
            Vai alla home
          </button>
        </div>
      </div>
    </div>
  );
}
